import { useState } from 'react';
import { Save, Globe, Mail, Phone, MapPin, Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';

export const SettingsTab = ({
  isArabic,
  settings,
  onSaveSettings,
  saving
}) => {
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');
  const [localSettings, setLocalSettings] = useState(settings);

  const handleChange = (field, value) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSaveSettings(localSettings);
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900">
          {isArabic ? 'إعدادات الموقع' : 'Site Settings'}
        </h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-xl hover:bg-slate-800 transition-colors disabled:opacity-50"
          data-testid="save-settings-btn"
        >
          <Save className="w-5 h-5" />
          {saving ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : (isArabic ? 'حفظ التغييرات' : 'Save Changes')}
        </button>
      </div>

      {/* Settings Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <SettingsTabButton
          active={activeSettingsTab === 'general'}
          onClick={() => setActiveSettingsTab('general')}
          icon={<Globe className="w-4 h-4" />}
          label={isArabic ? 'عام' : 'General'}
        />
        <SettingsTabButton
          active={activeSettingsTab === 'social'}
          onClick={() => setActiveSettingsTab('social')}
          icon={<Facebook className="w-4 h-4" />}
          label={isArabic ? 'التواصل الاجتماعي' : 'Social Media'}
        />
        <SettingsTabButton
          active={activeSettingsTab === 'seo'}
          onClick={() => setActiveSettingsTab('seo')}
          icon={<Globe className="w-4 h-4" />}
          label="SEO"
        />
      </div>

      {/* General Settings */}
      {activeSettingsTab === 'general' && (
        <div className="grid gap-6 max-w-2xl">
          <SettingsField
            label={isArabic ? 'رابط الشعار' : 'Logo URL'}
            icon={<Globe className="w-5 h-5" />}
            value={localSettings.logoUrl || ''}
            onChange={(v) => handleChange('logoUrl', v)}
            placeholder="https://..."
          />
          <SettingsField
            label={isArabic ? 'رقم الواتساب' : 'WhatsApp Number'}
            icon={<Phone className="w-5 h-5" />}
            value={localSettings.whatsappNumber || ''}
            onChange={(v) => handleChange('whatsappNumber', v)}
            placeholder="+218..."
          />
          <SettingsField
            label={isArabic ? 'البريد الإلكتروني' : 'Email'}
            icon={<Mail className="w-5 h-5" />}
            value={localSettings.email || ''}
            onChange={(v) => handleChange('email', v)}
            placeholder="email@example.com"
          />
          <SettingsField
            label={isArabic ? 'رقم الهاتف' : 'Phone Number'}
            icon={<Phone className="w-5 h-5" />}
            value={localSettings.phone || ''}
            onChange={(v) => handleChange('phone', v)}
            placeholder="+218..."
          />
          <SettingsField
            label={isArabic ? 'العنوان' : 'Address'}
            icon={<MapPin className="w-5 h-5" />}
            value={localSettings.address || ''}
            onChange={(v) => handleChange('address', v)}
            placeholder={isArabic ? 'طرابلس، ليبيا' : 'Tripoli, Libya'}
          />
        </div>
      )}

      {/* Social Media Settings */}
      {activeSettingsTab === 'social' && (
        <div className="grid gap-6 max-w-2xl">
          <SettingsField
            label="Facebook"
            icon={<Facebook className="w-5 h-5" />}
            value={localSettings.facebook || ''}
            onChange={(v) => handleChange('facebook', v)}
            placeholder="https://facebook.com/..."
          />
          <SettingsField
            label="Instagram"
            icon={<Instagram className="w-5 h-5" />}
            value={localSettings.instagram || ''}
            onChange={(v) => handleChange('instagram', v)}
            placeholder="https://instagram.com/..."
          />
          <SettingsField
            label="Twitter"
            icon={<Twitter className="w-5 h-5" />}
            value={localSettings.twitter || ''}
            onChange={(v) => handleChange('twitter', v)}
            placeholder="https://twitter.com/..."
          />
          <SettingsField
            label="LinkedIn"
            icon={<Linkedin className="w-5 h-5" />}
            value={localSettings.linkedin || ''}
            onChange={(v) => handleChange('linkedin', v)}
            placeholder="https://linkedin.com/..."
          />
          <SettingsField
            label="YouTube"
            icon={<Youtube className="w-5 h-5" />}
            value={localSettings.youtube || ''}
            onChange={(v) => handleChange('youtube', v)}
            placeholder="https://youtube.com/..."
          />
        </div>
      )}

      {/* SEO Settings */}
      {activeSettingsTab === 'seo' && (
        <div className="grid gap-6 max-w-2xl">
          <SettingsField
            label={isArabic ? 'عنوان الموقع (SEO)' : 'Site Title (SEO)'}
            icon={<Globe className="w-5 h-5" />}
            value={localSettings.seoTitle || ''}
            onChange={(v) => handleChange('seoTitle', v)}
            placeholder={isArabic ? 'البصمة الحديثة للمعدات الطبية' : 'ALBASMA Medical Equipment'}
          />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {isArabic ? 'وصف الموقع (SEO)' : 'Site Description (SEO)'}
            </label>
            <textarea
              value={localSettings.seoDescription || ''}
              onChange={(e) => handleChange('seoDescription', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder={isArabic ? 'وصف مختصر للموقع...' : 'Short site description...'}
            />
          </div>
          <SettingsField
            label={isArabic ? 'الكلمات المفتاحية' : 'Keywords'}
            icon={<Globe className="w-5 h-5" />}
            value={localSettings.seoKeywords || ''}
            onChange={(v) => handleChange('seoKeywords', v)}
            placeholder={isArabic ? 'معدات طبية، ليبيا، ...' : 'medical equipment, libya, ...'}
          />
        </div>
      )}
    </div>
  );
};

const SettingsTabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
      active 
        ? 'text-black border-b-2 border-black' 
        : 'text-slate-500 hover:text-black'
    }`}
  >
    {icon}
    {label}
  </button>
);

const SettingsField = ({ label, icon, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
    <div className="relative">
      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
        {icon}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-4 pr-12 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent"
        placeholder={placeholder}
      />
    </div>
  </div>
);

export default SettingsTab;
